import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { LoadingSpinner } from '@/components/common';
import axios from 'axios';

interface BrokerEntry {
  id: string;
  name: string;
  logo_url: string;
  description: string;
  phone: string;
  email: string;
  website: string;
  license_number: string;
  public_profile_slug: string;
}

export function BrokersDirectory() {
  const [brokers, setBrokers] = useState<BrokerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const loadBrokers = async () => {
      try {
        const response = await axios.get('/policy/api/public/brokers/');
        setBrokers(response.data.results || response.data);
      } catch {
        // silently fail
      } finally {
        setLoading(false);
      }
    };

    loadBrokers();
  }, []);

  const filteredBrokers = brokers.filter((broker) =>
    broker.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Insurance Brokers</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Find a trusted insurance broker near you
          </p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search brokers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full max-w-md rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2.5 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <LoadingSpinner />
          </div>
        ) : filteredBrokers.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-500 dark:text-gray-400 text-lg">
              {searchQuery ? 'No brokers match your search.' : 'No brokers available.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBrokers.map((broker) => (
              <Link
                key={broker.id}
                to={`/broker/${broker.public_profile_slug}`}
                className="bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-md transition-shadow p-6 flex flex-col"
              >
                <div className="flex items-center gap-4 mb-3">
                  {broker.logo_url ? (
                    <img
                      src={broker.logo_url}
                      alt={broker.name}
                      className="h-12 w-12 rounded-lg object-contain bg-gray-100 dark:bg-gray-700 p-1"
                    />
                  ) : (
                    <div className="h-12 w-12 rounded-lg bg-green-100 dark:bg-green-900 flex items-center justify-center">
                      <span className="text-lg font-bold text-green-600 dark:text-green-400">
                        {broker.name.charAt(0)}
                      </span>
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{broker.name}</h3>
                    {broker.license_number && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">License: {broker.license_number}</p>
                    )}
                  </div>
                </div>

                {broker.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-3">{broker.description}</p>
                )}

                <div className="mt-auto flex flex-col gap-1 text-sm text-gray-500 dark:text-gray-400">
                  {broker.phone && <span>Phone: {broker.phone}</span>}
                  {broker.email && <span>{broker.email}</span>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Powered by base.Policy.online
          </p>
        </div>
      </footer>
    </div>
  );
}
